// components/courses/feedback-form.tsx
"use client";
import { useEffect, useRef, useState, useTransition } from "react";
import Script from "next/script";
import { useTranslations } from "next-intl";
import { submitCourseFeedback } from "@/actions/course-feedback";
import { translateServerError } from "@/lib/courses/translate-server-error";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      reset: (id?: string) => void;
    };
  }
}

export function FeedbackForm({ slug }: { slug: string }) {
  const te = useTranslations("public.courses.errors");
  const tf = useTranslations("public.courses.feedbackForm");
  const [pending, start] = useTransition();
  const [form, setForm] = useState({ full_name: "", email: "", rating: 5, comment: "" });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  function set<K extends keyof typeof form>(k: K, v: typeof form[K]) { setForm((p) => ({ ...p, [k]: v })); }

  function renderTurnstile() {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || !window.turnstile) return;
    if (widgetIdRef.current) return;
    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (t) => setTurnstileToken(t),
    });
  }

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (window.turnstile) renderTurnstile();
  }, []);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    start(async () => {
      const r = await submitCourseFeedback(slug, { ...form, turnstile_token: turnstileToken });
      if (!r.success) setError(translateServerError(r.error, te));
      else setDone(true);
    });
  }

  if (done) {
    return (
      <p className="rounded border border-(--fm-border) bg-(--fm-surface) p-4 text-center text-sm">
        {tf("success")}
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="rounded bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p>}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{tf("ratingLabel")}</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => set("rating", n)}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            className={`text-xl ${n <= form.rating ? "text-amber-500" : "text-(--fm-text-secondary)"}`}>
            ★
          </button>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="space-y-1"><span className="text-sm font-medium">{tf("nameLabel")}</span>
          <input required className="w-full rounded border border-(--fm-border) px-3 py-2"
            value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></label>
        <label className="space-y-1"><span className="text-sm font-medium">{tf("emailLabel")}</span>
          <input required type="email" className="w-full rounded border border-(--fm-border) px-3 py-2"
            value={form.email} onChange={(e) => set("email", e.target.value)} /></label>
      </div>
      <label className="block space-y-1"><span className="text-sm font-medium">{tf("commentsLabel")}</span>
        <textarea rows={3} className="w-full rounded border border-(--fm-border) px-3 py-2"
          value={form.comment} onChange={(e) => set("comment", e.target.value)} /></label>
      {TURNSTILE_SITE_KEY && (
        <>
          <Script
            src="https://challenges.cloudflare.com/turnstile/v0/api.js"
            strategy="afterInteractive"
            onLoad={renderTurnstile}
          />
          <div ref={turnstileRef} />
        </>
      )}
      <button type="submit" disabled={pending || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
        className="rounded bg-(--fm-accent) px-4 py-2 text-sm text-(--fm-background) disabled:opacity-50">
        {pending ? tf("submitting") : tf("submitButton")}
      </button>
    </form>
  );
}
